import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Timeout para llamadas de IA SÍNCRONAS (legacy). Ya no se usa para acciones
// largas — se usa el patrón polling vía `runAiJob` (sin límite total).
export const AI_TIMEOUT_MS = 90_000;
// Timeout para CADA paso del polling (start + cada poll + cancel). Como las
// peticiones individuales son muy cortas, 30 s es más que de sobra.
export const AI_POLL_STEP_TIMEOUT_MS = 30_000;
export const AI_POLL_INTERVAL_MS = 2_000;

/**
 * Lanza una acción de IA en background y va consultando su estado hasta que
 * termina. Sin límite de tiempo total: la generación puede tardar lo que
 * necesite (segundos o minutos).
 *
 * @param {string} projectId
 * @param {string} action - "generate-sequence" | "generate-dialogue" | "rewrite" | "suggest-directions"
 * @param {object} params
 * @param {object} opts
 * @param {function} [opts.onProgress] - se invoca con `{elapsed_seconds, status}` en cada poll
 * @param {AbortSignal} [opts.signal] - permite cancelar
 * @returns {Promise<any>} el `result` del job cuando termina con éxito
 */
export async function runAiJob(projectId, action, params, { onProgress, signal } = {}) {
  const start = await api.post(
    `/icaa/projects/${projectId}/screenplay/ai/jobs/start`,
    { action, params },
    { timeout: AI_POLL_STEP_TIMEOUT_MS }
  );
  const jobId = start.data?.job_id;
  if (!jobId) {
    throw new Error("El servidor no devolvió un job_id.");
  }

  // Cancel listener: si el usuario aborta, llamamos al DELETE en best-effort.
  const cancel = async () => {
    try {
      await api.delete(
        `/icaa/projects/${projectId}/screenplay/ai/jobs/${jobId}`,
        { timeout: AI_POLL_STEP_TIMEOUT_MS }
      );
    } catch (_e) {
      // ignore — el job ya pudo terminar
    }
  };
  if (signal) {
    if (signal.aborted) {
      await cancel();
      throw new DOMException("Cancelado por el usuario", "AbortError");
    }
    signal.addEventListener("abort", cancel, { once: true });
  }

  // Bucle de polling.
  while (true) {
    if (signal?.aborted) {
      throw new DOMException("Cancelado por el usuario", "AbortError");
    }
    await new Promise((r) => setTimeout(r, AI_POLL_INTERVAL_MS));
    const r = await api.get(
      `/icaa/projects/${projectId}/screenplay/ai/jobs/${jobId}`,
      { timeout: AI_POLL_STEP_TIMEOUT_MS }
    );
    const job = r.data || {};
    if (onProgress) onProgress(job);
    if (job.status === "done") return job.result;
    if (job.status === "error") {
      const err = new Error(job.error || "La IA falló.");
      err.aiJobError = true;
      throw err;
    }
    if (job.status === "cancelled") {
      throw new DOMException("Cancelado por el usuario", "AbortError");
    }
    // Si está en pending o running → seguimos.
  }
}

/**
 * Convierte un error de axios en un mensaje claro y accionable.
 * Diferencia 4 escenarios: timeout, sin conexión, error servidor, error usuario.
 */
export function formatAxiosError(e) {
  // 1) Sin objeto error (caso extremo).
  if (!e) return "Error desconocido.";

  // 2) Cancelación explícita del usuario (AbortError) — no es realmente un error.
  if (e.name === "AbortError" || (e.message || "").toLowerCase().includes("cancelado")) {
    return "Cancelado.";
  }

  // 3) Error capturado del propio job de IA (lanzado por runAiJob).
  if (e.aiJobError) {
    const msg = e.message || "La IA falló.";
    // Detectamos errores de validación de negocio para mostrarlos tal cual.
    if (/falta|selecciona|obligatorio|inv[áa]lid/i.test(msg)) return msg;
    return `La IA devolvió un error: ${msg}`;
  }

  // 4) Timeout explícito de axios (paso individual del polling).
  if (e.code === "ECONNABORTED" || (e.message || "").toLowerCase().includes("timeout")) {
    return "El servidor tardó demasiado en responder en este paso. Reintenta.";
  }

  // 5) "Network Error" → proxy cerró la conexión, o sin red.
  if (e.message === "Network Error" || !e.response) {
    return "No se pudo conectar con el servidor. Comprueba tu conexión y vuelve a intentarlo.";
  }

  // 6) El backend respondió → usar el detail si lo tiene.
  const status = e.response.status;
  const detail = e.response.data?.detail;
  if (detail) return String(detail);

  if (status >= 500) {
    return `El servidor falló (${status}). Reintenta en unos segundos.`;
  }
  if (status >= 400) {
    return `Petición inválida (${status}). Revisa los datos del formulario.`;
  }
  return e.message || "Error desconocido.";
}

export const fmtEUR = (v) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Number(v || 0));

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};
